import { SimpleCarousel } from '@eventbrite/collection-carousel';
import { track } from '@eventbrite/datalayer-library';
import { FormattedEvent } from '@eventbrite/event-renderer';
import { gettext } from '@eventbrite/i18n';
import { LoadingSkeleton } from '@eventbrite/loading-skeleton';
import { logEvent } from '@eventbrite/statsig';
import React from 'react';
import { connect } from 'react-redux';
import DiscoverVerticalEventCard from '../../../../components/DiscoverEventCard/DiscoverVerticalEventCard';
import { ConnectedProps, DiscoverState } from '../../../../types/index';
import {
    HOMEPAGE_SEASONAL_CTR_CLICK,
    HOMEPAGE_SEASONAL_FEATURED,
} from '../../constants/analytics';
import { useCategoryEventsQuery } from '../../hooks/events/events';
import './SeasonalCarousel.scss';
import { EventLoadingState } from './SeasonalCarouselLoading';

export enum NavDirections {
    Left = 'left',
    Right = 'right',
}
const renderLoadingSkeleton = (isMobile: boolean) => {
    return (
        <div className="loader">
            <LoadingSkeleton height={isMobile ? '400px' : '426px'} />
        </div>
    );
};

const renderEventLoadingState = () => {
    return (
        <div className="eventLoader">
            <EventLoadingState numberOfEvents={5} />
        </div>
    );
};

export const SeasonalCarousel = ({
    isAuthenticated,
    shareOptions,
    locale,
    location,
    isMobile = false,
}: SeasonalCarouselProps) => {
    const [pageSize, setPageSize] = React.useState(1);
    const [pageIndex, setPageIndex] = React.useState(1);
    //todo : Update query parameters in  production
    const { data, isLoading } = useCategoryEventsQuery({
        queryOverload: {
            q: 'mardi',
            price: 'paid',
            page: pageIndex,
            page_size: 20,
            aggs: {},
            dates: ['current_future'],
        },
        refetchOnMount: false,
        location: location,
        enabled: true,
    });

    React.useEffect(() => {
        if (location && location.currentPlace) {
            track({
                eventName: 'SeasonalCarouselView',
                eventData: {
                    searchLocation: location.currentPlace,
                },
            });
        }
    }, [location]);

    if (isLoading && pageSize === 1) {
        return renderLoadingSkeleton(isMobile);
    } else if (isLoading) {
        return renderEventLoadingState();
    }

    const events: FormattedEvent[] = data?.events || [];

    if (events.length == 0) {
        return null;
    }

    const pageCount = data?.pagination?.page_count || 1;
    const moduleIndex = isMobile ? 20 : 5;
    const handleNavigationClick = (direction: string) => {
        direction === 'left'
            ? setPageSize(pageSize - 1)
            : setPageSize(pageSize + 1);
        pageSize % moduleIndex === 0 &&
            (direction === 'left'
                ? setPageIndex(pageIndex - 1)
                : setPageIndex(pageIndex + 1));

        logEvent(HOMEPAGE_SEASONAL_CTR_CLICK);
    };

    return (
        <div
            className="seasonal-carousel"
            data-heap-id="homepage-mardi-gras-carousel"
        >
            <section className="seasonal-carousel-container">
                <SimpleCarousel
                    headerTitle={
                        <div className="title-subtitle-container">
                            <h2>{gettext('Mardi gras events ')} &#127881;</h2>
                        </div>
                    }
                    slidesVisible={{ desktop: 4, tablet: 3 }}
                    onNavigationClick={handleNavigationClick}
                    lazilyRenderOffScreenElements={true}
                    fullyEnableNavigation={true}
                    isFirstPage={pageIndex === 1}
                    isLastPage={pageIndex === pageCount}
                >
                    {events.map((event) => (
                        <div
                            className="seasonal-carousel-event-card"
                            key={event.id}
                        >
                            <DiscoverVerticalEventCard
                                locale={locale || 'en_US'}
                                isLazyImage={true}
                                isAuthenticated={isAuthenticated || false}
                                event={event}
                                affCode="ehometext"
                                statsigLocationString={
                                    HOMEPAGE_SEASONAL_FEATURED
                                }
                                data-testid={`home-event-card-${event.id}`}
                                shareOptions={shareOptions}
                                hideUrgencySignals={false}
                            />
                        </div>
                    ))}
                </SimpleCarousel>
            </section>
        </div>
    );
};

const _mapStateToProps = ({
    app,
    user,
    location,
    shareOptions,
}: DiscoverState) => {
    return {
        isMobile: app.isMobile,
        isAuthenticated: user.isAuthenticated,
        location: location,
        shareOptions: shareOptions,
        locale: app.locale,
    };
};

type SeasonalCarouselProps = ConnectedProps<typeof _mapStateToProps>;
export default connect(_mapStateToProps)(SeasonalCarousel);
